// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import {Lazy} from "@swim/util";
import type {Class} from "@swim/util";
import type {Mapping} from "@swim/util";
import {Model} from "@swim/model";
import {TraitModelRef} from "@swim/model";
import type {Graphics} from "@swim/graphics";
import {PolygonIcon} from "@swim/graphics";
import {Status} from "@nstream/domain";
import type {EntityTraitObserver} from "@nstream/domain";
import {EntityTrait} from "@nstream/domain";
import {LanesRelationTrait} from "../lane/LanesRelationTrait";
import {AgentAspectTrait} from "./AgentAspectTrait";
import {AgentRelationTrait} from "./AgentRelationTrait";

/** @public */
export interface AgentEntityTraitObserver<T extends AgentEntityTrait = AgentEntityTrait> extends EntityTraitObserver<T> {
}

/** @public */
export class AgentEntityTrait extends EntityTrait {
  constructor() {
    super();
    this.icon.setIntrinsic(AgentEntityTrait.icon);
    this.searchable.set(true);
  }

  declare readonly observerType?: Class<AgentEntityTraitObserver>;

  @TraitModelRef({
    modelType: Model,
    modelKey: "portal",
    traitType: AgentAspectTrait,
    traitKey: "aspect",
  })
  readonly portal!: TraitModelRef<this, AgentAspectTrait>;

  @TraitModelRef({
    modelType: Model,
    modelKey: "agents",
    get traitType(): typeof AgentRelationTrait {
      return AgentRelationTrait;
    },
    traitKey: "relation",
  })
  readonly agentsRelation!: TraitModelRef<this, AgentRelationTrait>;

  @TraitModelRef({
    modelType: Model,
    modelKey: "lanes",
    traitType: LanesRelationTrait,
    traitKey: "relation",
  })
  readonly lanesRelation!: TraitModelRef<this, LanesRelationTrait>;

  static readonly alertStatus: Mapping<number, Status> = Status.worsening(0, 100_000, 1_000_000, 10_000_000, 100_000_000);

  @Lazy
  static get icon(): Graphics {
    return PolygonIcon.create(6);
  }
}
